import { formatDateTimeShort, formatIsoDateShort } from "@/lib/bookingDates";
import { pitchUsageTypeLabel } from "@/lib/pitchUsageType";
import { ACTION_LABELS, ENTITY_TYPE_LABELS } from "@/types/audit";

export function entityTypeLabel(entityType: string): string {
  return ENTITY_TYPE_LABELS[entityType] ?? entityType;
}

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function formatAuditTimestamp(iso: string): string {
  return formatDateTimeShort(iso);
}

/** Liest einen lesbaren Anzeigewert aus Audit-JSON. */
export function formatAuditFieldValue(value: unknown, field?: string): string {
  if (value === null || value === undefined) return "—";
  if (field === "usage_type") {
    return pitchUsageTypeLabel(String(value));
  }
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatIsoDateShort(value);
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDateTimeShort(value);
  }
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      if (value.length === 0) return "—";
      return JSON.stringify(value, null, 2);
    }
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

const FIELD_LABELS: Record<string, string> = {
  usage_type: "Platztyp",
  code: "Code",
  label: "Bezeichnung",
  is_active: "Aktiv",
  is_blocked: "Gesperrt",
  has_electricity: "Strom",
  has_water: "Wasser",
  has_sewage: "Abwasser",
  reason: "Begründung",
  pitch_id: "Stellplatz",
  pitch_assignment_mode: "Zuweisungsmodus",
  invoices_retained: "Belege aufbewahrt",
  bookings_retained: "Aufenthalte aufbewahrt",
  privacy_erased_at: "Gelöscht am",
};

export function auditFieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

/** Felder, die bei der Änderungsanzeige ausgeblendet werden (Metadaten). */
const HIDDEN_DIFF_KEYS = new Set([
  "id",
  "tenant_id",
  "created_at",
  "updated_at",
  "version",
]);

export function diffAuditValues(
  oldValues: Record<string, unknown> | null,
  newValues: Record<string, unknown> | null,
): { field: string; before: string; after: string }[] {
  const keys = new Set<string>();
  if (oldValues) Object.keys(oldValues).forEach((k) => keys.add(k));
  if (newValues) Object.keys(newValues).forEach((k) => keys.add(k));

  const rows: { field: string; before: string; after: string }[] = [];
  for (const key of [...keys].sort()) {
    if (HIDDEN_DIFF_KEYS.has(key)) continue;
    const before = oldValues?.[key];
    const after = newValues?.[key];
    if (JSON.stringify(before) === JSON.stringify(after)) continue;
    rows.push({
      field: auditFieldLabel(key),
      before: formatAuditFieldValue(before, key),
      after: formatAuditFieldValue(after, key),
    });
  }
  return rows;
}
